import { useBetSliderStore } from '@stores/betSliderStore'
import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { SlotContent } from '@utils/Slot'
import React, { useEffect, useState } from 'react'
import JackpotStatisticsItem from '../common/JackpotStatisticsItem'
import { jackpotLinkConfig } from './config'
import { JackpotLinkStonesType, JackpotLinkTypeIndexMap, JackpotLinkTypes } from 'game-ui-types'
import { getBetValue } from '@utils/betOption'

interface JackpotLinkStatisticsItemProps {
    type: JackpotLinkTypes
}

/**
 * JackpotLinkStatisticsItem Component
 *
 * Renders and toggles title between default and hold & spin mode. Generates picked stones. Passes stats data to generic jackpot statistics item.
 */
const JackpotLinkStatisticsItem: React.FC<JackpotLinkStatisticsItemProps> = ({ type }) => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const betOptions = useConfigurationStore((state) => state.betOptions)

    const viewportConfig = jackpotLinkConfig[type][viewport]
    const jackpotLinkStatistics = useJackpotStore((state) => state.jackpotLinkStatistics)
    const isJackpotLinkHoldSpin = useJackpotStore((state) => state.isJackpotLinkHoldSpin)
    const pickedStones = useJackpotStore((state) => state.pickedStones)

    const currentBetIndex = useBetSliderStore((state) => state.currentIndex)

    const [isFrozen, setIsFrozen] = useState(false)
    const [amount, setAmount] = useState(-1)
    const [isAmountStatic, setIsAmountStatic] = useState(false)
    const [blinkingUser, setBlinkingUser] = useState('')
    const [isBlinking, setIsBlinking] = useState(false)
    const [lastWinAmount, setLastWinAmount] = useState(0)
    const [lastWinUser, setLastWinUser] = useState('')

    const handleBlinkingComplete = () => {
        setIsBlinking(false)

        if (!isFrozen) {
            setIsAmountStatic(true)
            setAmount(jackpotLinkStatistics.levels[JackpotLinkTypeIndexMap[type]].balance)
        }
    }

    const handleBlinkingUserToggled = () => {
        // Intentionally left empty
    }

    useEffect(() => {
        switch (type) {
            case JackpotLinkTypes.GRAND:
            case JackpotLinkTypes.MAJOR: {
                const levelStatistics = jackpotLinkStatistics.levels[JackpotLinkTypeIndexMap[type]]

                if (!isBlinking) {
                    setAmount(levelStatistics.balance)
                }

                const previousWinAmount = lastWinAmount
                const previousWinUser = lastWinUser

                setLastWinAmount(levelStatistics.lastWin.winAmount)
                setLastWinUser(levelStatistics.lastWin.winUser)

                const frozen =
                    previousWinAmount !== levelStatistics.lastWin.winAmount ||
                    previousWinUser !== levelStatistics.lastWin.winUser

                if (isFrozen) {
                    setBlinkingUser(levelStatistics.lastWin.winUser)
                    setAmount(levelStatistics.lastWin.winAmount)
                    setIsBlinking(true)
                }

                setIsFrozen(frozen)
                break
            }
            case JackpotLinkTypes.MINOR:
            case JackpotLinkTypes.MINI: {
                const betSectors = jackpotLinkStatistics.schemaSettings.betSectors
                const betValue = getBetValue(betOptions[currentBetIndex]) ?? 0
                const betSector = betSectors.find(
                    (s) => s.betRanges[0] <= betValue && s.betRanges[s.betRanges.length - 1] >= betValue
                )

                setAmount((betSector ?? betSectors[0])[type.toLowerCase() as 'mini' | 'minor'])
                break
            }
        }
    }, [jackpotLinkStatistics, betOptions, currentBetIndex])

    const generateTitle = () => {
        const { titles: titlesConfig } = viewportConfig.extended
        let src = viewportConfig.extended.titles.images.default
        let titleStyle: Record<string, string> = {}

        if (titlesConfig.align === 'left') {
            titleStyle = { left: '0' }
        } else if (titlesConfig.align === 'right') {
            titleStyle = { right: '0' }
        } else {
            titleStyle = {
                left: '50%',
                transform: 'translateX(-50%)',
            }
        }

        titleStyle.top = `-${viewportConfig.jackpotStatistics.amountBox.top}px`

        const imgStyle = {
            height: `${titlesConfig.height}px`,
        }

        if (isJackpotLinkHoldSpin && viewportConfig.extended.titles.images.holdSpin) {
            src = viewportConfig.extended.titles.images.holdSpin
        }

        return (
            <div className="title absolute" style={titleStyle}>
                <img style={imgStyle} src={src} />
            </div>
        )
    }

    const generateStones = () => {
        const { stone: stoneConfig } = viewportConfig.extended

        if (!stoneConfig.positions) {
            return <></>
        }

        return stoneConfig.positions.map((position, i) => {
            // If stone not picked do not show anything
            if (i >= pickedStones[type as JackpotLinkStonesType]) {
                return <></>
            }

            const stoneStyle = {
                top: `${position.y}px`,
                left: `${position.x}px`,
            }
            const imgStyle = {
                width: `${stoneConfig.dimensions}px`,
            }

            return (
                <div className={`stone-${type.toLowerCase()} fixed`} style={stoneStyle}>
                    <img style={imgStyle} src={stoneConfig.images.fullStone} />
                </div>
            )
        })
    }

    return (
        <JackpotStatisticsItem
            config={jackpotLinkConfig[type]}
            type={type}
            amount={amount}
            winningUser={blinkingUser}
            isAmountStatic={isAmountStatic}
            shouldUseOdometer={false}
            onBlinkingComplete={handleBlinkingComplete}
            onWinningUserPresentationComplete={handleBlinkingUserToggled}
        >
            <SlotContent slot="above">
                {generateTitle()}
                {generateStones()}
            </SlotContent>
        </JackpotStatisticsItem>
    )
}

export default JackpotLinkStatisticsItem
